/**
 * FCMTokenHandler
 *
 * FCM 토큰의 생성, 저장, 갱신, 삭제를 처리합니다.
 * 로컬 스토리지와 백엔드 API 연동을 담당합니다.
 */

import axios from 'axios';
import { API_HOST } from '../utils/variables';

// 로컬 스토리지 키
const STORAGE_KEYS = {
  FCM_TOKEN: 'fcm_token', TOKEN_CREATED_AT: 'fcm_token_created_at',
};

// 백엔드 API 엔드포인트
const API_ENDPOINTS = {
  SAVE_TOKEN: `${API_HOST}/v1/public/fcm-tokens`,
  DELETE_TOKEN: (token) => `${API_HOST}/v1/public/fcm-tokens/${encodeURIComponent(token)}`,
  UPDATE_TOKEN: (token) => `${API_HOST}/v1/public/fcm-tokens/${encodeURIComponent(token)}`,
};

class FCMTokenHandler {
  constructor() {
    this.currentToken = null;
    this.platform = 'web';
  }

  /**
   * 토큰 생성
   *
   * Firebase Messaging을 통해 FCM 토큰을 생성합니다.
   *
   * @param {Messaging} messaging - Firebase Messaging 인스턴스
   * @param {string} vapidKey - VAPID 공개 키
   * @param {ServiceWorkerRegistration} serviceWorkerRegistration - Service Worker 등록 객체
   * @returns {Promise<string|null>} FCM 토큰 또는 null
   */
  async generateToken(messaging, vapidKey, serviceWorkerRegistration) {
    try {
      const { getToken } = await import('firebase/messaging');

      const token = await getToken(messaging, {
        vapidKey, serviceWorkerRegistration,
      });

      if (token) {
        this.currentToken = token;

        // 로컬 스토리지에 저장
        FCMTokenHandler.setStoredToken(token);

        return token;
      }
      return null;
    } catch (error) {
      console.error('[FCMTokenHandler] 토큰 생성 중 오류:', error);
      return null;
    }
  }

  /**
   * 백엔드에 토큰 저장
   */
  async saveTokenToBackend(token, userId) {
    try {
      if (!userId) {
        return false;
      }

      const payload = {
        token,
        platform: this.platform,
        userId,
        createdAt: new Date().toISOString(),
      };

      const response = await axios.post(API_ENDPOINTS.SAVE_TOKEN, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 200 || response.status === 201 || response.status === 409) {
        return true;
      }
      return false;
    } catch (error) {
      console.error('[FCMTokenHandler] 백엔드 토큰 저장 중 오류:', error);
      return false;
    }
  }

  /**
   * 토큰 갱신
   */
  async refreshToken(newToken, userId) {
    try {
      const tokenToUpdate = FCMTokenHandler.getStoredToken();

      if (!tokenToUpdate) {
        return this.saveTokenToBackend(newToken, userId);
      }

      const payload = {
        newToken, platform: this.platform, updatedAt: new Date().toISOString(),
      };

      const response = await axios.put(API_ENDPOINTS.UPDATE_TOKEN(tokenToUpdate), payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 200) {
        FCMTokenHandler.setStoredToken(newToken);
        this.currentToken = newToken;
        return true;
      }

      return false;
    } catch (error) {
      console.error('토큰 갱신 실패:', error);
      if (error.response?.status === 404) {
        return this.saveTokenToBackend(newToken, userId);
      }
      return false;
    }
  }

  /**
   * 백엔드에서 토큰 삭제
   * 로그아웃 시 백엔드에서 FCM 토큰을 삭제합니다.
   */
  async deleteTokenFromBackend(token = null) {
    try {
      const tokenToDelete = token || FCMTokenHandler.getStoredToken();
      if (!tokenToDelete) return false;

      const response = await axios.delete(API_ENDPOINTS.DELETE_TOKEN(tokenToDelete));

      if (response.status === 200 || response.status === 204 || response.status === 404) {
        FCMTokenHandler.clearStoredToken();
        this.currentToken = null;
        return true;
      }

      return false;
    } catch (error) {
      console.error('토큰 삭제 실패:', error);
      if (error.response?.status === 404) {
        FCMTokenHandler.clearStoredToken();
        return true;
      }
      return false;
    }
  }

  static getStoredToken() {
    return localStorage.getItem(STORAGE_KEYS.FCM_TOKEN);
  }

  static setStoredToken(token) {
    localStorage.setItem(STORAGE_KEYS.FCM_TOKEN, token);
  }

  static clearStoredToken() {
    localStorage.removeItem(STORAGE_KEYS.FCM_TOKEN);
  }

  getCurrentToken() {
    return this.currentToken || FCMTokenHandler.getStoredToken();
  }

  static getUserIdFromStorage() {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || user._id || user.userId || null;
      }
    } catch (error) {
      console.error('사용자 ID 조회 실패:', error);
    }
    return null;
  }
}

// 싱글톤 인스턴스 생성
const fcmTokenHandler = new FCMTokenHandler();

export default fcmTokenHandler;
